import {useState, useMemo, useEffect, useRef} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {useSearchParams, useLocation} from 'react-router-dom'

import {useScrollPosition} from '../../hooks'

import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import DetailPageImageContainer from './DetailPageImageContainer'



const styles = {
  root: css`
    width: 100%;
    max-width: 100vw;
    min-height: calc(100vh + 10px);
    background-color: #000;
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    align-items: stretch;
    justify-content: stretch;
    overflow: hidden;

    @media (max-width: 600px) {
      overflow: clip;
    }
  `,
  detailModal: css`
    position: fixed;
    top: 0;
  `,
  top: css`
    width: 100%;
    display: flex;
    justify-content: center;
    color: #fff;
    padding: 170px 25px 100px 25px;
    min-height: 50%;
    background-color: #000;

    @media (max-width: 1120px) {
      padding: 0px 25px 20px 25px;
      margin: 80px 0 30px 0px;
    }

    @media (max-width: 768px) {
      padding: 0;
      margin-top: 60px;
    }
  `,
  topContent: css`
    display: flex;
    justify-content: center;
    width: 100%;
    align-items: flex-start;
    max-width: 1100px;

    @media (max-width: 1120px) {
      flex-direction: column;
      align-items: center;
      max-width: 550px;
    }
  `,
  leftSide: css`
    width: 100%;
    flex-grow: 1;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 15px;
    z-index: 9;

    @media (max-width: 1120px) {
      max-width: 550px;
    }
  `,
  rightSide: css`
    width: 100%;
    flex-grow: 1;
    min-width: 450px;
    max-width: 500px;
    padding: 0 15px 0 40px;
    z-index: 10;

    @media (max-width: 1120px) {
      margin-top: -30px;
      padding: 0;
      min-width: 0;
      max-width: 100%;
    }
  `,
  middle: css`
    position: sticky;
    width: 100%;
    height: 50px;
    z-index: 50;
    background-color: #000;
    top: 60px;

    @media (max-width: 1120px) {
      top: 124px;
    }

    @media (max-width: 600px) {
      top: 60px;
    }
  `,
  middleContent: css`
    margin: 0 auto;
    width: 100%;
    max-width: 1100px;
    height: 100%;
    color: #fff;
    display: flex;
    justify-content: center;
  `,
  tabsIndicator: css`
    & .MuiTabs-indicator {
      background-color: #ff0095;
    }
  `,
  tab: css`
    &.MuiTab-root {
      font-weight: 400;
      color: #fff;
      font-size: 0.9rem;
      text-transform: uppercase;
      min-width: 70px;
      transition: font-size 0.2s ease-in-out;
    }

    &.Mui-selected {
      font-weight: 600;
      font-size: 1rem;
    }

    @media (max-width: 1000px) {
      &.MuiTab-root {
        font-size: 0.8rem;
      }
    }
  `,
  bottom: css`
    width: 100%;
    max-width: 100vw;
    display: flex;
    justify-content: center;
    background-color: #fff;
    flex-grow: 1;
  `,
  bottomContent: css`
    width: 100%;
    max-width: 1100px;
    min-height: calc(100vh - 110px);
    margin: 0 auto;
    display: flex;
    align-items: flex-start;

    @media (max-width: 1120px) {
      padding: 0 25px;
    }

    @media (max-width: 600px) {
      padding: 0 0 150px 0;
    }
  `,
  tabPanel: css`
    width: 100%;
  `
}



// Breakpoints
const BP_SMALL  = 600
const BP_MEDIUM = 768
const BP_LARGE  = 1120




const TabPanel = ({ children, value, index, ...props }) => {
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    if (value === index) setIsMounted(true)
  }, [value, index])
  return (
    <div 
      hidden={value !== index} 
      role='tabpanel' 
      id={`nav-tabpanel-${index}`}
      css={styles.tabPanel}
    >  
      {isMounted && children}
    </div>
  )
}





// let scrollPositions = window.sessionStorage.getItem('scrollPositions') || {}
// scrollPositions = (typeof scrollPositions === 'string') ? JSON.parse(scrollPositions) : scrollPositions 



const DetailPage = ({
  type,
  image,
  imageSrcset,
  video,
  originalImage,
  Broadcast,
  MainButton,
  information,
  tab1,
  tab1Label,
  tab2,
  tab2Label,
  tab3,
  tab3Label,
  tab4,
  tab4Label,
  tab5,
  tab5Label,
}) => {
  // Refs
  const topRef = useRef(null)
  const tabsRef = useRef(null)
  const rootRef = useRef(null)

  // URL & scroll
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const scrollPosition = useScrollPosition()

  // Media queries
  const isLg = useMediaQuery(`(min-width:${BP_LARGE}px)`)
  const isXs = useMediaQuery(`(max-width:${BP_SMALL}px)`)
  const isMd = useMediaQuery(`(max-width:${BP_MEDIUM}px)`)

  // Tabs state
  const initialTab = Number(searchParams.get('tab'))
  const [tab, setTab] = useState(Number.isFinite(initialTab) ? initialTab : 0)

  // Memoized tab arrays
  const tabs = useMemo(
    () => [tab1, tab2, tab3, tab4, tab5].filter(Boolean),
    [tab1, tab2, tab3, tab4, tab5]
  )
  const tabLabels = useMemo(
    () => [tab1Label, tab2Label, tab3Label, tab4Label, tab5Label].filter(Boolean),
    [tab1Label, tab2Label, tab3Label, tab4Label, tab5Label]
  )

  const aspect = ('performance' === type) ? '3:4' : '1:1'

  // Min heights for scroll restore
  const [minHeight, setMinHeight] = useState(0)

  // Restore scroll position
  useEffect(() => {
    const store = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}')
    const prev = store[location.key] || 0
    setMinHeight(prev + window.innerHeight)
  }, [location.key])

  // Save scroll on change
  useEffect(() => {
    const store = JSON.parse(sessionStorage.getItem('scrollPositions') || '{}')
    store[location.key] = window.pageYOffset
    sessionStorage.setItem('scrollPositions', JSON.stringify(store))
  }, [scrollPosition, location.key])



  // Calculate sticky header minHeight
  useEffect(() => {
    if (!tabs.length || !topRef.current) return
    const header = document.getElementById('header')
    const rect = topRef.current.getBoundingClientRect()
    const value = window.innerHeight + rect.top - (header?.offsetHeight || 0)
    setMinHeight(prev => Math.max(prev, value))
  }, [tabs.length])

  // Tab change
  const handleTabChange = (e, newValue) => {
    setTab(newValue)
    setSearchParams({ tab: newValue }, { replace: true })
  }


  return (
      <div css={styles.root} ref={rootRef} style={{ minHeight }}>
        <div css={styles.detailModal} />

        <section css={styles.top} ref={topRef}>
          <div css={styles.topContent}>
            <div
              css={styles.leftSide}
              style={{maxWidth: type === 'performance' ? '450px' : '550px'}}
            >
              <DetailPageImageContainer
                type={type}
                video={video}
                poster={image || originalImage}
                aspect={aspect}
                MainButton={MainButton}
              />
            </div>

            <div
              css={styles.rightSide}
              style={{
                marginTop:
                  type === 'performance' && !isLg ? '-60px' : '-30px',
              }}
            >
              {information}
            </div>
          </div>
        </section>

        <section css={styles.middle}>
          <div css={styles.middleContent} ref={tabsRef}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              textColor='secondary'
              variant={isXs ? 'fullWidth' : 'standard'}
              indicatorColor='secondary'
              css={styles.tabsIndicator}
            >
              {tabLabels.map((label, i) => (
                <Tab key={i} label={label} disableRipple css={styles.tab} />
              ))}
            </Tabs>
          </div>
        </section>


        {tabs.length > 0 &&
          <section css={styles.bottom}>
            <div css={styles.bottomContent}>
              {tabs.map((content, i) => (
                <TabPanel key={i} value={tab} index={i}>
                  {content}
                </TabPanel>
              ))}
            </div>
          </section>
        }
      </div>
  )
}



export default DetailPage






